// The controller working position: top bar (METAR, ATIS, QNH, runway), surface movement radar and/or radar
// display, electronic flight strips, radio transcript, command line with push-to-talk, and the instruction
// panel for the selected aircraft.
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { GameSession } from '../game/session';
import type { Cmd } from '../sim/commands';
import type { Aircraft } from '../sim/aircraft';
import type { Pilot } from '../sim/pilot';
import type { Role } from '../data/types';
import { GroundRadar } from './scope/groundRadar';
import { RadarScope } from './scope/radarScope';
import { metar } from '../sim/weather';
import { Radio } from '../audio/radio';
import { PushToTalk, speechSupported } from '../audio/speech';
import { fmtTime } from './screens';
import { distOnFinal } from '../sim/aircraft';
import { NM, dist } from '../sim/geo';

const GROUND_ROLES: Role[] = ['DEL', 'APN', 'GND', 'TWR'];
const RADAR_ROLES: Role[] = ['DEP', 'APP', 'DIR', 'CTR'];
const hhmmss = (d: Date) => `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}:${String(d.getUTCSeconds()).padStart(2, '0')}`;

function useSessionVersion(s: GameSession): number {
  const [v, setV] = useState(0);
  useEffect(() => s.subscribe(() => setV(s.version)), [s]);
  return v;
}

export function Workstation({ session, onSettings, onEnd }: { session: GameSession; onSettings: () => void; onEnd: () => void }) {
  useSessionVersion(session);
  const w = session.world;
  const [toast, setToast] = useState<{ text: string; kind: 'bad' | 'info' } | null>(null);
  const [selected, setSelectedState] = useState<string | null>(null);
  const [routePreview, setRoutePreview] = useState<number[] | null>(null);
  const [ctx, setCtx] = useState<{ cs: string; x: number; y: number } | null>(null);
  const roles = session.level.roles;
  const hasGround = roles.some(r => GROUND_ROLES.includes(r));
  const hasRadar = roles.some(r => RADAR_ROLES.includes(r));
  const hasTower = roles.includes('TWR');
  const showToast = useCallback((text: string, kind: 'bad' | 'info' = 'bad') => {
    setToast({ text, kind });
    window.setTimeout(() => setToast(t => (t?.text === text ? null : t)), 5000);
  }, []);
  const select = useCallback((cs: string | null) => { session.selected = cs; setSelectedState(cs); setCtx(null); session.notify(); }, [session]);

  useEffect(() => {
    session.onEnd = onEnd;
    session.start();
    const offTx = w.onTransmission(tx => {
      const mine = w.playerUnits().includes(tx.freq);
      if (mine) Radio.play(tx, session.timeScale);
    });
    const offEv = w.onEvent(e => {
      if (e.kind === 'safety' && (e.sev === 'major' || e.sev === 'critical')) { Radio.alert(); showToast(e.text); }
      else if (e.sev === 'major') showToast(e.text);
    });
    return () => { offTx(); offEv(); session.stop(); Radio.stopAll(); };
  }, [session, w, onEnd, showToast]);

  // keyboard: Esc deselects, Enter focuses the command line
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { select(null); setCtx(null); }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [select]);

  const issue = useCallback((cs: string, cmds: Cmd[]) => {
    const warn = session.issue(cs, cmds);
    if (warn) showToast(warn, 'info');
  }, [session, showToast]);

  const split = (hasGround && hasRadar) || hasTower;
  return (
    <div className="ws">
      <TopBar session={session} onSettings={onSettings} onEnd={() => session.finish()} />
      <div className="ws-main">
        <div className={`ws-left ${split ? 'split' : ''}`}>
          {(hasGround) && <GroundScope session={session} onSelect={select} preview={routePreview} onContext={(cs, x, y) => cs && setCtx({ cs, x, y })} />}
          {(hasRadar || hasTower) && <RadarView session={session} onSelect={select} range={hasRadar ? 38 : 9} towerMode={!hasRadar} onHeading={(cs, h) => issue(cs, [{ k: 'HDG', hdg: h }])} onContext={(cs, x, y) => cs && setCtx({ cs, x, y })} />}
        </div>
        <div className="ws-right"><Strips session={session} selected={selected} onSelect={select} /></div>
      </div>
      <div className="ws-bottom">
        <div className="radio">
          <RadioLog session={session} />
          <CommandLine session={session} onIssue={issue} onToast={showToast} />
        </div>
        <ActionPanel session={session} selected={selected} onIssue={issue} onPreview={setRoutePreview} />
      </div>
      {ctx && <ContextMenu session={session} at={ctx} onIssue={issue} onClose={() => setCtx(null)} />}
      {toast && <div className={`toast ${toast.kind === 'info' ? 'info' : ''}`}>{toast.text}</div>}
    </div>
  );
}

// ======================================================================== top bar
function TopBar({ session, onSettings, onEnd }: { session: GameSession; onSettings: () => void; onEnd: () => void }) {
  const w = session.world;
  const utc = w.utc();
  const units = w.playerUnits().map(u => w.unit(u));
  const sc = w.score.summary();
  const wind = w.weather.windKt < 1 ? 'calm' : `${String(Math.round(w.trueToMag(w.weather.windDir) / 10) * 10 || 360).padStart(3, '0')}/${w.weather.windKt}${w.weather.gustKt ? 'G' + w.weather.gustKt : ''}`;
  const tc = session.level.assists.timeControl;
  return (
    <div className="ws-top">
      <div className="cell"><span className="k">{session.airport.icao} · {session.level.id}</span><span className="v">{units.map(u => `${u.callsign} ${u.freq}`).join(' · ')}</span></div>
      <div className="cell"><span className="k">UTC</span><span className="v">{hhmmss(utc)}</span></div>
      <div className="cell"><span className="k">ATIS</span><span className="v">{w.atis.letter}</span></div>
      <div className="cell"><span className="k">QNH · TL</span><span className="v">{w.weather.qnh} · {w.tl}</span></div>
      <div className="cell"><span className="k">RWY</span><span className="v">{[...new Set([...w.config.arr, ...w.config.dep])].join('/')}{w.lvp ? ' LVP' : ''}</span></div>
      <div className="cell"><span className="k">Wind</span><span className="v">{wind}</span></div>
      <div className="cell grow"><span className="k">METAR</span><span className="v metar">{metar(session.airport.icao, w.weather, utc)}</span></div>
      <div className="cell"><span className="k">Restzeit</span><span className="v">{fmtTime(session.remaining)}</span></div>
      <div className="cell"><span className="k">Wertung</span><span className="v scorepill"><span>S <b>{sc.safety}</b></span><span>E <b>{sc.efficiency}</b></span><span>K <b>{sc.comms}</b></span></span></div>
      <div className="tools">
        {tc && [1, 2, 4].map(x => <button key={x} className={`btn tiny ${session.timeScale === x ? 'on' : ''}`} onClick={() => session.setTimeScale(x)}>{x}×</button>)}
        {tc && <button className={`btn tiny ${session.paused ? 'on' : ''}`} onClick={() => session.togglePause()}>{session.paused ? 'Weiter' : 'Pause'}</button>}
        <button className="btn tiny" onClick={onSettings}>⚙</button>
        <button className="btn tiny danger" onClick={() => { if (confirm('Schicht jetzt beenden und auswerten?')) onEnd(); }}>Schicht beenden</button>
      </div>
    </div>
  );
}

// ======================================================================== scopes
function GroundScope({ session, onSelect, preview, onContext }: { session: GameSession; onSelect: (cs: string | null) => void; preview: number[] | null; onContext: (cs: string | null, x: number, y: number) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const gr = useRef<GroundRadar | null>(null);
  useEffect(() => {
    const g = new GroundRadar(ref.current!, session);
    gr.current = g;
    let raf = 0;
    const frame = () => { g.draw(); raf = requestAnimationFrame(frame); };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); g.dispose(); };
  }, [session]);
  useEffect(() => { if (gr.current) { gr.current.onSelect = onSelect; gr.current.onContext = (cs, x, y) => { const r = ref.current!.getBoundingClientRect(); onContext(cs, r.left + x, r.top + y); }; } }, [onSelect, onContext]);
  useEffect(() => { if (gr.current) gr.current.routePreview = preview; }, [preview]);
  return <div className="scope"><canvas ref={ref} /><span className="scope-tag">Bodenlagebild · {session.airport.icao}</span></div>;
}

function RadarView({ session, onSelect, range, towerMode, onHeading, onContext }: { session: GameSession; onSelect: (cs: string | null) => void; range: number; towerMode: boolean; onHeading: (cs: string, h: number) => void; onContext: (cs: string | null, x: number, y: number) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const sc = useRef<RadarScope | null>(null);
  const [mva, setMva] = useState(false);
  const [vec, setVec] = useState(1);
  useEffect(() => {
    const r = new RadarScope(ref.current!, session, range);
    sc.current = r;
    if (towerMode) {
      // tower: centred slightly into the final approach
      const end = session.airport.end(session.world.config.arr[0]!);
      const crs = (end.meta.ils?.course ?? end.hdg) * Math.PI / 180;
      r.home.cx = end.thr.x - Math.sin(crs) * 6 * NM; r.home.cy = end.thr.y - Math.cos(crs) * 6 * NM;
      r.applyHome();
    }
    let raf = 0;
    const frame = () => { r.draw(); raf = requestAnimationFrame(frame); };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); r.dispose(); };
  }, [session, range, towerMode]);
  useEffect(() => {
    if (!sc.current) return;
    sc.current.onSelect = onSelect;
    sc.current.onHeadingTo = onHeading;
    sc.current.onContext = (cs, x, y) => { const r = ref.current!.getBoundingClientRect(); onContext(cs, r.left + x, r.top + y); };
  }, [onSelect, onHeading, onContext]);
  useEffect(() => { if (sc.current) { sc.current.showMva = mva; sc.current.vectorMin = vec; } }, [mva, vec]);
  return (
    <div className="scope">
      <canvas ref={ref} />
      <span className="scope-tag">{towerMode ? 'Anflug · Lagebild' : 'Radar'} · {session.world.unit(session.world.unitFor('APP') ?? session.world.playerUnits()[0]!).callsign}</span>
      <div className="scope-tools">
        <button className={`btn tiny ${mva ? 'on' : ''}`} onClick={() => setMva(!mva)}>MVA</button>
        {[1, 2, 3].map(v => <button key={v} className={`btn tiny ${vec === v ? 'on' : ''}`} onClick={() => setVec(v)}>{v}′</button>)}
      </div>
    </div>
  );
}

// ======================================================================== strips
interface StripInfo { ac: Aircraft; p: Pilot; bay: 'call' | 'mine' | 'next'; call?: string }

function relevantNext(session: GameSession, p: Pilot): boolean {
  const w = session.world;
  const mine = (r: Role) => w.isPlayerRole(r);
  const ac = p.ac;
  if (mine('GND') && p.isDeparture && (p.stage === 'CLEARED' || p.stage === 'PUSH_REQ') && p.freq !== w.unitFor('GND')) return true;
  if (mine('TWR') && p.isDeparture && (p.stage === 'TAXIING' || p.stage === 'AT_HOLD') && p.freq !== w.unitFor('TWR')) return true;
  if (mine('TWR') && !p.isDeparture && !ac.onGround && p.arrRwy && ac.locCap && distOnFinal(ac, w.airport.end(p.arrRwy)) < 16) return true;
  if (mine('GND') && !p.isDeparture && p.stage === 'LANDED') return true;
  if ((mine('APP') || mine('DIR')) && !p.isDeparture && !ac.onGround && p.freq === w.unitFor('CTR')) return true;
  if (mine('DEP') && p.isDeparture && (p.stage === 'LINEUP' || p.stage === 'TAKEOFF' || (p.stage === 'CLIMB' && p.freq === w.unitFor('TWR')))) return true;
  if (mine('CTR') && p.isDeparture && !ac.onGround && p.freq === w.unitFor('DEP') && dist(ac.x, ac.y, 0, 0) > 15 * NM) return true;
  return false;
}

function Strips({ session, selected, onSelect }: { session: GameSession; selected: string | null; onSelect: (cs: string | null) => void }) {
  const w = session.world;
  const units = w.playerUnits();
  const strips: StripInfo[] = [];
  for (const p of w.pilots.values()) {
    const req = w.requests.find(r => r.cs === p.cs && units.includes(r.unit));
    if (req) strips.push({ ac: p.ac, p, bay: 'call', call: req.kind });
    else if (p.freq && units.includes(p.freq)) strips.push({ ac: p.ac, p, bay: 'mine' });
    else if (relevantNext(session, p)) strips.push({ ac: p.ac, p, bay: 'next' });
  }
  const calls = strips.filter(s => s.bay === 'call').sort((a, b) => (w.requests.find(r => r.cs === a.ac.cs)?.t ?? 0) - (w.requests.find(r => r.cs === b.ac.cs)?.t ?? 0));
  const mine = strips.filter(s => s.bay === 'mine');
  const next = strips.filter(s => s.bay === 'next');
  return (
    <>
      <Bay title="Anrufe" count={calls.length} items={calls} session={session} selected={selected} onSelect={onSelect} grow={false} />
      <Bay title="Auf deiner Frequenz" count={mine.length} items={mine} session={session} selected={selected} onSelect={onSelect} grow />
      <Bay title="Erwartet" count={next.length} items={next} session={session} selected={selected} onSelect={onSelect} grow={false} />
      <Hints session={session} />
    </>
  );
}

function Bay({ title, count, items, session, selected, onSelect, grow }: { title: string; count: number; items: StripInfo[]; session: GameSession; selected: string | null; onSelect: (cs: string | null) => void; grow: boolean }) {
  return (
    <div className="bay" style={grow ? { flex: 1, minHeight: 80 } : { maxHeight: '34%' }}>
      <div className="bay-h"><span>{title}</span><span>{count}</span></div>
      <div className="bay-list">{items.map(s => <Strip key={s.ac.cs} s={s} session={session} sel={selected === s.ac.cs} onSelect={onSelect} />)}</div>
    </div>
  );
}

const CALL_TEXT: Record<string, string> = {
  CLEARANCE: 'Freigabe?', PUSH: 'Pushback?', STARTUP: 'Anlassen?', TAXI: 'Rollen?', DEPARTURE: 'Abflugbereit', CHECKIN_DEP: 'Erstanruf', CHECKIN_ARR: 'Erstanruf',
  CHECKIN_FINAL: 'Endanflug', VACATED: 'Piste frei', CHECKIN: 'Meldung', LANDING: 'Landung?', GOAROUND: 'Durchstart', SAY_AGAIN: '?',
};

function statusText(session: GameSession, s: StripInfo): string {
  const { ac, p } = s;
  const w = session.world;
  switch (p.stage) {
    case 'PRE': return 'an Position';
    case 'CLEARED': return 'freigegeben';
    case 'PUSH_REQ': return 'wartet Push';
    case 'PUSHING': return 'Pushback';
    case 'STARTING': return 'lässt an';
    case 'TAXI_REQ': return 'rollbereit';
    case 'TAXIING': return ac.waitingAt()?.startsWith('RWY') ? 'hält vor Piste' : ac.holdPosition ? 'hält' : `rollt → ${p.depHold?.name ?? ''}`;
    case 'AT_HOLD': return `Rollhalt ${p.depHold?.name ?? ''}`;
    case 'LINEUP': return ac.gmode === 'LINED_UP' ? 'aufgerollt' : 'rollt auf';
    case 'TAKEOFF': return 'Startlauf';
    case 'CLIMB': case 'ENROUTE': return `steigt ${Math.round(ac.altFt / 100)}`;
    case 'ARRIVING': return ac.amode === 'HOLD' ? 'Warteverfahren' : ac.locCap ? 'LOC' : `${Math.round(dist(ac.x, ac.y, 0, 0) / NM)} NM`;
    case 'FINAL': return p.clearedLand ? `Landung frei · ${p.arrRwy && distOnFinal(ac, w.airport.end(p.arrRwy)).toFixed(1)} NM` : `Endanflug ${p.arrRwy && distOnFinal(ac, w.airport.end(p.arrRwy)).toFixed(1)} NM`;
    case 'LANDED': return ac.gmode === 'ROLLOUT' ? 'Ausrollen' : 'abgerollt';
    case 'TAXI_IN': return `rollt → P${ac.fp.stand ?? ''}`;
    case 'PARKED': return 'abgestellt';
    default: return p.stage;
  }
}

function Strip({ s, session, sel, onSelect }: { s: StripInfo; session: GameSession; sel: boolean; onSelect: (cs: string | null) => void }) {
  const { ac, p } = s;
  const fp = ac.fp;
  const w = session.world;
  const dep = p.isDeparture;
  const vfr = fp.rules === 'V';
  const rflWarn = dep && fp.error === 'RFL_PARITY';
  return (
    <div className={`strip ${vfr ? 'vfr' : dep ? 'dep' : 'arr'} ${sel ? 'sel' : ''} ${s.bay === 'call' ? 'call' : ''}`} onClick={() => onSelect(sel ? null : ac.cs)}>
      <span className="bar5" />
      <div>
        <div className="cs">{ac.cs}</div>
        <div>{fp.type}/{ac.type.wake} {dep ? `→ ${fp.dest}` : `← ${fp.dep}`} <span className={rflWarn ? 'warn' : ''}>FL{fp.rfl}</span></div>
        <div>{dep ? (p.clearance ? `${p.clearance.sid} · A${p.clearance.altFt / 100}` : `${fp.exitFix ?? ''} · sq ${fp.squawk ?? '----'}`) : `${fp.entryFix ?? ''} · ${p.arrRwy ?? ''} · P${fp.stand ?? '--'}`}</div>
      </div>
      <div className="r">
        <div>{dep ? `P${fp.stand ?? ''}` : `ATIS ${p.atisLetter}`}</div>
        <div className="mono">{ac.squawk}</div>
        <div className="st">{s.call ? CALL_TEXT[s.call] ?? s.call : statusText(session, s)}</div>
        {!dep && p.atisLetter !== w.atis.letter && p.freq && w.playerUnits().includes(p.freq) && <div className="warn">ATIS alt</div>}
      </div>
    </div>
  );
}

function Hints({ session }: { session: GameSession }) {
  const hs = session.hints.slice(-2).reverse();
  if (!hs.length) return null;
  return (
    <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid var(--line)' }}>
      {hs.map(h => <div key={h.id} className="hint"><div className="who">Ausbilderin</div>{h.text}</div>)}
    </div>
  );
}

// ======================================================================== radio
function RadioLog({ session }: { session: GameSession }) {
  const w = session.world;
  const ref = useRef<HTMLDivElement>(null);
  const units = w.playerUnits();
  const showOther = Radio.s.otherFreqs;
  const items = w.log.filter(t => showOther || units.includes(t.freq)).slice(-120);
  const last = items[items.length - 1]?.id;
  useEffect(() => { const el = ref.current; if (el) el.scrollTop = el.scrollHeight; }, [last]);
  const start = w.options.startUtc.getTime();
  return (
    <div className="radio-log" ref={ref}>
      {!items.length && <div className="muted" style={{ fontSize: 12 }}>Funkstille. Anrufe der Piloten erscheinen hier – und sind zu hören.</div>}
      {items.map(t => {
        const live = w.t < t.t + t.dur;
        const mine = units.includes(t.freq);
        return (
          <div key={t.id} className={`tx ${t.from === 'ATC' ? 'atc' : 'pilot'} ${t.from === 'ATC' && !t.byPlayer ? 'ai' : ''} ${mine ? '' : 'other'} ${live ? 'live' : ''}`}>
            <span className="t">{hhmmss(new Date(start + t.t * 1000))}</span>
            <span className="f">{w.unit(t.freq).freq}</span>
            <span className="m">{t.from === 'PILOT' ? '' : '▸ '}{t.text}</span>
          </div>
        );
      })}
    </div>
  );
}

function CommandLine({ session, onIssue, onToast }: { session: GameSession; onIssue: (cs: string, cmds: Cmd[]) => void; onToast: (t: string, k?: 'bad' | 'info') => void }) {
  const [text, setText] = useState('');
  const [hist, setHist] = useState<string[]>([]);
  const [hi, setHi] = useState(-1);
  const [ptt, setPtt] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const rec = useMemo(() => new PushToTalk(), []);
  const parsed = text.trim() ? session.parse(text) : null;
  const preview = parsed && parsed.cs && parsed.cmds.length ? session.preview(parsed.cs, parsed.cmds) : '';
  const err = parsed ? (!parsed.cs ? 'Rufzeichen?' : parsed.unknown.length ? `nicht verstanden: ${parsed.unknown.join(' ')}` : '') : '';

  const send = useCallback((t: string) => {
    const r = session.parse(t);
    if (!r.cs) { onToast('Kein Rufzeichen erkannt – Flugzeug auswählen oder Rufzeichen voranstellen.', 'info'); return false; }
    if (!r.cmds.length) { onToast(`Keine Anweisung erkannt${r.unknown.length ? `: „${r.unknown.join(' ')}“` : ''}.`, 'info'); return false; }
    onIssue(r.cs, r.cmds);
    setHist(h => [t, ...h].slice(0, 40));
    setHi(-1);
    return true;
  }, [session, onIssue, onToast]);

  useEffect(() => {
    rec.onInterim = t => setText(t);
    rec.onFinal = t => { setPtt(false); setText(t); if (send(t)) setText(''); };
    rec.onError = m => { setPtt(false); onToast(m, 'info'); };
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && document.activeElement !== input.current && !(document.activeElement instanceof HTMLInputElement) && !e.repeat && speechSupported()) { e.preventDefault(); Radio.click(true); setPtt(true); rec.start(); }
      else if (e.key === 'Enter' && document.activeElement !== input.current && !(document.activeElement instanceof HTMLInputElement)) { input.current?.focus(); }
    };
    const up = (e: KeyboardEvent) => { if (e.code === 'Space' && rec.active) { e.preventDefault(); Radio.click(false); rec.stop(); } };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [rec, send, onToast]);

  return (
    <div className="cmd">
      <div className="cmd-row">
        <input ref={input} value={text} placeholder={session.selected ? `${session.selected} … (z. B. „L270 A40“, „taxi to holding point F via N C“)` : 'Rufzeichen + Anweisung, z. B. „DLH4TK cleared to land runway 26“ – Leertaste: Sprechen'}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { if (send(text)) setText(''); }
            else if (e.key === 'ArrowUp') { const n = Math.min(hist.length - 1, hi + 1); if (hist[n]) { setHi(n); setText(hist[n]!); } e.preventDefault(); }
            else if (e.key === 'ArrowDown') { const n = hi - 1; setHi(Math.max(-1, n)); setText(n >= 0 ? hist[n]! : ''); e.preventDefault(); }
            else if (e.key === 'Escape') { setText(''); input.current?.blur(); }
          }} />
        <button className={`btn ptt ${ptt ? 'live' : ''}`} disabled={!speechSupported()} title={speechSupported() ? 'Gedrückt halten zum Sprechen (oder Leertaste)' : 'Spracherkennung wird von diesem Browser nicht unterstützt'}
          onPointerDown={() => { Radio.click(true); setPtt(true); rec.start(); }} onPointerUp={() => { Radio.click(false); rec.stop(); }} onPointerLeave={() => { if (rec.active) { rec.stop(); } }}>
          {ptt ? '● SENDEN' : 'PTT'}
        </button>
      </div>
      <div className={`preview ${err ? 'err' : ''}`}>{err || (preview ? `▸ ${preview}` : ' ')}</div>
    </div>
  );
}

// ======================================================================== instructions
interface Action { label: string; cmds: Cmd[]; primary?: boolean; title?: string }

/** Instructions that fit the selected aircraft's situation and the player's role on its frequency. */
function actionsFor(session: GameSession, cs: string): { role: Role | null; actions: Action[]; onMyFreq: boolean } {
  const w = session.world;
  const p = w.pilot(cs);
  if (!p) return { role: null, actions: [], onMyFreq: false };
  const ac = p.ac;
  const units = w.playerUnits();
  const onMyFreq = !!p.freq && units.includes(p.freq);
  const unitRoles = p.freq ? w.unitRoles(p.freq).filter(r => w.isPlayerRole(r)) : [];
  const has = (r: Role) => unitRoles.includes(r) || (!onMyFreq && w.isPlayerRole(r));
  const A: Action[] = [];
  const req = w.requests.find(r => r.cs === cs);
  const contact = (role: Role, label: string) => { const u = w.unitFor(role); if (u && u !== p.freq) A.push({ label, cmds: [{ k: 'CONTACT', unit: u }] }); };
  let role: Role | null = null;
  if (p.isDeparture && ac.onGround) {
    if (has('DEL') && (p.stage === 'PRE')) {
      role = 'DEL';
      const c = w.defaultClearance(p);
      if (c) A.push({ label: 'Freigabe', cmds: [c], primary: req?.kind === 'CLEARANCE', title: `${c.sid}, ${c.altFt} ft, squawk ${c.squawk}` });
      if (p.clearance) A.push({ label: 'Readback korrekt', cmds: [{ k: 'RB_OK' }], primary: true });
    }
    if (has('GND') || has('APN')) {
      role = role ?? 'GND';
      if (ac.gmode === 'PARKED' && (p.stage === 'PUSH_REQ' || p.stage === 'CLEARED' || p.stage === 'PRE')) {
        if (ac.stand?.push === false) A.push({ label: 'Anlassen frei', cmds: [{ k: 'STARTUP' }], primary: req?.kind === 'STARTUP' });
        else A.push({ label: 'Push & Start', cmds: [{ k: 'PUSH' }], primary: req?.kind === 'PUSH' });
      }
      if (['TAXI_REQ', 'TAXIING', 'AT_HOLD', 'STARTING'].includes(p.stage)) {
        A.push({ label: 'Halten', cmds: [{ k: 'HOLD_POS' }] }, { label: 'Weiterrollen', cmds: [{ k: 'CONT_TAXI' }] });
        const wait = ac.waitingAt();
        if (wait?.startsWith('RWY:')) { const rw = w.airport.runways.find(r => r.id === wait.slice(4)); if (rw) A.push({ label: `Piste ${rw.id} kreuzen`, cmds: [{ k: 'CROSS', rwy: rw.endIds[0] }], primary: true }); }
        contact('TWR', 'Kontakt Tower');
      }
    }
    if (has('TWR') && ['TAXIING', 'AT_HOLD', 'LINEUP', 'TAKEOFF'].includes(p.stage)) {
      role = 'TWR';
      const rwy = p.depRwy ?? w.config.dep[0]!;
      const end = w.airport.end(rwy);
      const inter = p.depHold && p.depHold !== end.depHolds[0] ? p.depHold.name : undefined;
      if (p.stage !== 'LINEUP' && p.stage !== 'TAKEOFF') A.push({ label: 'Line up', cmds: [{ k: 'LINEUP', rwy, hold: inter }] });
      if (p.stage !== 'TAKEOFF') A.push({ label: 'Start frei', cmds: [{ k: 'TAKEOFF', rwy }], primary: req?.kind === 'DEPARTURE' || ac.gmode === 'LINED_UP' });
      if (p.clearedTakeoff) A.push({ label: 'Start abbrechen', cmds: [{ k: 'CANCEL_TAKEOFF' }] });
      A.push({ label: 'Halten', cmds: [{ k: 'HOLD_POS' }] });
    }
  }
  if (p.isDeparture && !ac.onGround) {
    if (has('TWR')) { role = 'TWR'; contact('DEP', 'Kontakt Radar'); }
  }
  if (!p.isDeparture) {
    if (has('TWR') && (p.stage === 'FINAL' || p.stage === 'ARRIVING') && !ac.onGround) {
      role = 'TWR';
      const rwy = p.arrRwy ?? w.config.arr[0]!;
      A.push({ label: 'Landung frei', cmds: [{ k: 'LAND', rwy }], primary: req?.kind === 'CHECKIN_FINAL' || req?.kind === 'LANDING' });
      A.push({ label: 'Weiter Anflug', cmds: [{ k: 'CONTINUE' }] }, { label: 'Durchstarten', cmds: [{ k: 'GO_AROUND' }] });
      if (ac.goAround) contact('APP', 'Kontakt Radar');
    }
    if (has('TWR') && ac.onGround) {
      role = 'TWR';
      const end = w.airport.end(p.arrRwy ?? w.config.arr[0]!);
      if (ac.gmode === 'ROLLOUT') for (const ex of end.exits.filter(e => e.along > w.airport.along(end, ac) + 80)) A.push({ label: `Abrollen ${ex.name}`, cmds: [{ k: 'VACATE', via: ex.name }] });
      contact('GND', 'Kontakt Ground');
    }
    if ((has('GND') || has('APN')) && ac.onGround && (p.stage === 'LANDED' || p.stage === 'TAXI_IN')) {
      role = 'GND';
      A.push({ label: 'Halten', cmds: [{ k: 'HOLD_POS' }] }, { label: 'Weiterrollen', cmds: [{ k: 'CONT_TAXI' }] });
      const wait = ac.waitingAt();
      if (wait?.startsWith('RWY:')) { const rw = w.airport.runways.find(r => r.id === wait.slice(4)); if (rw) A.push({ label: `Piste ${rw.id} kreuzen`, cmds: [{ k: 'CROSS', rwy: rw.endIds[0] }], primary: true }); }
    }
  }
  if (!ac.onGround && unitRoles.some(r => RADAR_ROLES.includes(r))) {
    role = unitRoles.find(r => RADAR_ROLES.includes(r)) ?? 'APP';
    if (req && (req.kind === 'CHECKIN_ARR' || req.kind === 'CHECKIN_DEP' || req.kind === 'CHECKIN')) A.push({ label: 'Identified', cmds: [{ k: 'IDENTIFIED' }], primary: true });
    if (!p.isDeparture) { contact('TWR', 'Kontakt Tower'); }
    else { contact('CTR', 'Kontakt Center'); }
    if (p.isDeparture && role === 'CTR') { /* center keeps departures */ }
    if (!p.isDeparture && role === 'CTR') contact('APP', 'Kontakt Approach');
    A.push({ label: 'Eigennavigation', cmds: [{ k: 'RESUME_OWN' }] }, { label: 'Speed frei', cmds: [{ k: 'SPD_OWN' }] });
  }
  return { role, actions: A, onMyFreq };
}

function ActionPanel({ session, selected, onIssue, onPreview }: { session: GameSession; selected: string | null; onIssue: (cs: string, cmds: Cmd[]) => void; onPreview: (p: number[] | null) => void }) {
  const w = session.world;
  const [compose, setCompose] = useState<Cmd[]>([]);
  const [hdg, setHdg] = useState('');
  const [alt, setAlt] = useState('');
  const [spd, setSpd] = useState('');
  const [fix, setFix] = useState('');
  const [hold, setHold] = useState('');
  const [via, setVia] = useState('');
  const [stand, setStand] = useState('');
  const [sid, setSid] = useState('');
  useEffect(() => { setCompose([]); setHdg(''); setAlt(''); setSpd(''); setFix(''); setHold(''); setVia(''); setStand(''); setSid(''); onPreview(null); }, [selected, onPreview]);
  const p = selected ? w.pilot(selected) : undefined;
  const ac = p?.ac;
  // taxi suggestion
  const taxiTarget = useMemo(() => {
    if (!p || !ac?.onGround) return null;
    if (p.isDeparture && ['TAXI_REQ', 'TAXIING', 'AT_HOLD', 'STARTING', 'PUSHING'].includes(p.stage)) {
      const end = w.airport.end(p.depRwy ?? w.config.dep[0]!);
      const h = hold || w.chooseDepHold(p)?.name || end.depHolds[0]?.name;
      return h ? { kind: 'HOLD' as const, rwy: end.id, hold: h } : null;
    }
    if (!p.isDeparture && (p.stage === 'LANDED' || p.stage === 'TAXI_IN')) {
      const st = stand || ac.fp.stand;
      return st ? { kind: 'STAND' as const, stand: st } : null;
    }
    return null;
  }, [selected, hold, stand, p?.stage, session.version]);
  const suggested = useMemo(() => (p && taxiTarget ? w.suggestTaxiVia(p, taxiTarget) : null), [p, taxiTarget, w]);
  const viaList = (via.trim() ? via.toUpperCase().split(/[\s,]+/).filter(Boolean) : suggested) ?? [];
  const showRoute = useCallback(() => {
    if (!p || !taxiTarget) return;
    const start = w.groundStartNode(p.ac);
    const to = taxiTarget.kind === 'HOLD' ? w.airport.depHold(taxiTarget.rwy, taxiTarget.hold)?.node : w.airport.stands[taxiTarget.stand]?.node;
    if (start === null || to === undefined) return;
    const path = w.airport.findPath(start, to, { via: viaList, span: p.ac.type.spanM, operator: p.ac.fp.airline, stands: [p.ac.stand?.id, taxiTarget.kind === 'STAND' ? taxiTarget.stand : undefined].filter((x): x is string => !!x), extraNames: taxiTarget.kind === 'HOLD' ? [taxiTarget.hold] : [] });
    onPreview(path);
  }, [p, taxiTarget, viaList, w, onPreview]);

  if (!selected || !p || !ac) {
    return (
      <div className="panel">
        <h6>Anweisungen</h6>
        <div className="muted" style={{ fontSize: 12.5 }}>Wähle ein Flugzeug auf dem Schirm oder in den Streifen. Anweisungen werden sofort gesendet – mit <b>Shift</b>+Klick sammelst du mehrere zu einem Funkspruch.</div>
        <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>Kurzbefehle: <span className="mono">C</span> Freigabe · <span className="mono">RB</span> Readback korrekt · <span className="mono">PB</span> Pushback · <span className="mono">T F via N C</span> Rollen · <span className="mono">LU</span> · <span className="mono">CTO</span> · <span className="mono">CTL</span> · <span className="mono">GA</span> · <span className="mono">L270 / R090 / H180</span> · <span className="mono">A40 / FL100</span> · <span className="mono">S180</span> · <span className="mono">D NASUK</span> · <span className="mono">ILS 26</span> · <span className="mono">CT TWR</span></div>
      </div>
    );
  }
  const { role, actions, onMyFreq } = actionsFor(session, selected);
  const send = (cmds: Cmd[], e?: React.MouseEvent) => {
    if (e?.shiftKey) { setCompose(c => [...c, ...cmds]); return; }
    onIssue(selected, [...compose, ...cmds]);
    setCompose([]);
    onPreview(null);
  };
  const radar = !ac.onGround && !!role && RADAR_ROLES.includes(role);
  const levels = [2000, 2500, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000, 14000, 15000, 17000, 19000, 21000, 23000, 24000, 25000, 27000, 29000, 31000, 33000, 35000, 37000];
  const ta = w.meta.transitionAltFt;
  const levelCmd = (ft: number): Cmd => (ft > ta ? { k: 'ALT', ft, fl: true } : { k: 'ALT', ft, fl: false });
  const fixes = Object.keys(w.meta.fixes);
  const depEnd = w.airport.end(p.depRwy ?? w.config.dep[0]!);
  const sids = w.meta.sids.filter(s => s.rwy === depEnd.id);
  const showClearanceEditor = role === 'DEL' && p.stage === 'PRE';
  const def = showClearanceEditor ? w.defaultClearance(p) : null;
  return (
    <div className="panel">
      <div className="sel-head"><b>{ac.cs}</b><span className="muted" style={{ fontSize: 12 }}>{ac.fp.telephony || 'Kennzeichen'} · {ac.type.name}</span></div>
      {!onMyFreq && <div className="hint" style={{ background: '#2b2420', borderColor: '#5a4632', color: '#f2d7b4' }}>Nicht auf deiner Frequenz{p.freq ? ` (${w.unitCallsign(p.freq)} ${w.unitFreq(p.freq)})` : ''} – der Pilot hört dich nicht.</div>}
      {compose.length > 0 && <div className="compose">▸ {session.preview(selected, compose)} <button className="btn tiny" onClick={() => send([])}>Senden</button> <button className="btn tiny ghost" onClick={() => setCompose([])}>×</button></div>}
      {showClearanceEditor && def && (
        <>
          <h6>Streckenfreigabe</h6>
          <div className="inline-in">
            <select value={sid || def.sid} onChange={e => setSid(e.target.value)} style={{ width: 110 }}>{sids.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}</select>
            <span className="mono" style={{ fontSize: 12 }}>{def.altFt} ft · {def.squawk}</span>
            <button className="btn primary small" onClick={e => send([{ ...def, sid: sid || def.sid }], e)}>Freigabe</button>
          </div>
          {ac.fp.error === 'RFL_PARITY' && <div className="hint" style={{ background: '#2b2420', borderColor: '#5a4632', color: '#f2d7b4' }}>FL{ac.fp.rfl} passt nicht zur Halbkreisflugregel für die Flugrichtung – im Flugplan korrigieren lassen.
            <div style={{ marginTop: 4 }}><button className="btn tiny" onClick={() => { ac.fp.rfl += ac.fp.rfl > 300 ? -10 : 10; ac.fp.error = undefined; session.notify(); }}>Flugplan korrigieren</button></div></div>}
        </>
      )}
      <div className="act-grid">
        {actions.map(a => <button key={a.label} className={`btn ${a.primary ? 'primary' : ''}`} title={a.title ?? session.preview(selected, a.cmds)} onClick={e => send(a.cmds, e)}>{a.label}</button>)}
      </div>
      {taxiTarget && (
        <>
          <h6>Rollen</h6>
          <div className="inline-in" style={{ flexWrap: 'wrap' }}>
            {taxiTarget.kind === 'HOLD' ? (
              <select value={hold || taxiTarget.hold} onChange={e => setHold(e.target.value)}>{depEnd.depHolds.map(h => <option key={h.name} value={h.name}>{h.name} · {h.tora[depEnd.id]} m</option>)}</select>
            ) : (
              <select value={stand || taxiTarget.stand} onChange={e => setStand(e.target.value)}>{Object.values(w.airport.stands).filter(s => s.maxSpan >= ac.type.spanM).map(s => <option key={s.id} value={s.id}>P{s.id}{w.standOcc.has(s.id) && w.standOcc.get(s.id) !== ac.cs ? ' (belegt)' : ''}</option>)}</select>
            )}
            <input placeholder={suggested?.join(' ') ?? 'via'} value={via} onChange={e => setVia(e.target.value)} onFocus={showRoute} style={{ width: 110 }} />
            <button className="btn small primary" onMouseEnter={showRoute} onMouseLeave={() => onPreview(null)} onClick={e => send([{ k: 'TAXI', target: taxiTarget, via: viaList }], e)}>Rollen</button>
          </div>
          <div className="faint" style={{ fontSize: 11 }}>Route: {viaList.join(' → ') || '–'} (Vorschlag des Systems, überschreibbar)</div>
        </>
      )}
      {radar && (
        <>
          <h6>Radar</h6>
          <div className="inline-in">
            <input placeholder="Kurs" value={hdg} onChange={e => setHdg(e.target.value.replace(/\D/g, '').slice(0, 3))} />
            <button className="btn tiny" disabled={!hdg} onClick={e => send([{ k: 'HDG', hdg: Number(hdg) % 360 || 360, turn: 'L' }], e)}>links</button>
            <button className="btn tiny" disabled={!hdg} onClick={e => send([{ k: 'HDG', hdg: Number(hdg) % 360 || 360, turn: 'R' }], e)}>rechts</button>
            <button className="btn tiny" disabled={!hdg} onClick={e => send([{ k: 'HDG', hdg: Number(hdg) % 360 || 360 }], e)}>Kurs</button>
          </div>
          <div className="inline-in">
            <select value={alt} onChange={e => setAlt(e.target.value)}><option value="">Höhe</option>{levels.map(l => <option key={l} value={l}>{l > ta ? `FL${l / 100}` : `${l} ft`}</option>)}</select>
            <button className="btn tiny" disabled={!alt} onClick={e => send([levelCmd(Number(alt))], e)}>Höhe</button>
            <select value={spd} onChange={e => setSpd(e.target.value)}><option value="">Speed</option>{[160, 170, 180, 190, 200, 210, 220, 230, 250, 280].map(s => <option key={s} value={s}>{s} kt</option>)}</select>
            <button className="btn tiny" disabled={!spd} onClick={e => send([{ k: 'SPD', kt: Number(spd) }], e)}>Speed</button>
          </div>
          <div className="inline-in">
            <select value={fix} onChange={e => setFix(e.target.value)}><option value="">Fix</option>{fixes.map(f => <option key={f} value={f}>{f}</option>)}</select>
            <button className="btn tiny" disabled={!fix} onClick={e => send([{ k: 'DIRECT', fix }], e)}>Direkt</button>
            <button className="btn tiny" disabled={!fix || !w.meta.holdings.some(h => h.fix === fix)} onClick={e => send([{ k: 'HOLD_AT', fix }], e)}>Warten</button>
            {!p.isDeparture && <button className="btn tiny" onClick={e => send([{ k: 'ILS', rwy: p.arrRwy ?? w.config.arr[0]! }], e)}>ILS {p.arrRwy ?? w.config.arr[0]}</button>}
          </div>
          <div className="faint" style={{ fontSize: 11 }}>Tipp: Rechtsklick auf die Karte gibt dem ausgewählten Flugzeug einen Steuerkurs dorthin.</div>
        </>
      )}
    </div>
  );
}

function ContextMenu({ session, at, onIssue, onClose }: { session: GameSession; at: { cs: string; x: number; y: number }; onIssue: (cs: string, cmds: Cmd[]) => void; onClose: () => void }) {
  const { actions } = actionsFor(session, at.cs);
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [onClose]);
  return (
    <div className="ctxmenu" style={{ left: Math.min(at.x, window.innerWidth - 210), top: Math.min(at.y, window.innerHeight - 40 - actions.length * 32) }} onMouseDown={e => e.stopPropagation()}>
      <div className="h">{at.cs}</div>
      {actions.length ? actions.map(a => <button key={a.label} onClick={() => { onIssue(at.cs, a.cmds); onClose(); }}>{a.label}</button>) : <div className="h">Keine passende Anweisung</div>}
    </div>
  );
}
